import { useSiteContent } from '@/hooks/useSiteContent';
import ContentState from '@/components/feature/ContentState';
import { useTranslation } from 'react-i18next';

interface VisaHighlightData {
  title: string;
  highlight: string;
  description: string;
  image: string;
  features: { icon: string; title: string; description: string }[];
}

export default function VisaHighlight() {
  const { data, loading, error, refresh } = useSiteContent<VisaHighlightData>(
    'special_services_visa',
  );
  const { t } = useTranslation();

  if (loading || error || !data) {
    return <ContentState loading={loading} error={error} ready={!!data} onRetry={refresh} />;
  }

  return (
    <section className="w-full bg-white py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Image */}
          <div className="order-2 lg:order-1">
            <div className="relative w-full h-72 md:h-96 overflow-hidden rounded-xl">
              <img
                src={data.image}
                alt={t('ss.visaAlt')}
                title={t('ss.visaTitle')}
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
              {data.title}{' '}
              <span className="text-primary-500">{data.highlight}</span>
            </h2>
            <p className="text-foreground-600 leading-relaxed mb-8">
              {data.description}
            </p>

            <div className="space-y-5">
              {data.features.map((feature) => (
                <div key={feature.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                    <i className={`${feature.icon} text-lg`} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-foreground-950">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}